// =============================================================
// LIGHTBOX — tam ekran görüntüleyici · erişilebilir diyalog
// - Escape kapatır, ok tuşları gezdirir, backdrop tıklaması kapatır
// - Focus trap + odak geri yükleme, body scroll kilidi
// - prefers-reduced-motion: anında geçiş (CSS tarafında)
// =============================================================

import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./Lightbox.module.css";

export interface LightboxImage {
  readonly src: string;
  readonly alt: string;
  readonly caption?: string;
  readonly subcaption?: string;
}

interface LightboxProps {
  images: readonly LightboxImage[];
  /** null = kapalı */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  label?: string;
}

export function Lightbox({ images, index, onClose, onNavigate, label = "Görsel galerisi" }: LightboxProps) {
  const open = index !== null;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const goPrev = useCallback(() => {
    if (index === null || images.length === 0) return;
    onNavigate((index - 1 + images.length) % images.length);
  }, [index, images.length, onNavigate]);

  const goNext = useCallback(() => {
    if (index === null || images.length === 0) return;
    onNavigate((index + 1) % images.length);
  }, [index, images.length, onNavigate]);

  // Açılış: odak yönetimi + scroll kilidi · Kapanış: geri yükleme
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.setAttribute("data-lightbox-open", "");

    closeRef.current?.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.removeAttribute("data-lightbox-open");
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
    };
  }, [open]);

  // Klavye: Escape / ok tuşları / Tab focus trap
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, goPrev, goNext]);

  if (!open || index === null) return null;

  const current = images[index];
  if (!current) return null;
  const many = images.length > 1;

  return createPortal(
    <div
      ref={dialogRef}
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        ref={closeRef}
        type="button"
        className={styles.lightbox__close}
        onClick={onClose}
        aria-label="Kapat"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {many && (
        <button
          type="button"
          className={`${styles.lightbox__nav} ${styles["lightbox__nav--prev"]}`}
          onClick={goPrev}
          aria-label="Önceki görsel"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </button>
      )}

      <figure className={styles.lightbox__stage}>
        <img
          key={current.src}
          className={styles.lightbox__img}
          src={current.src}
          alt={current.alt}
          decoding="async"
        />
        {(current.caption || current.subcaption || many) && (
          <figcaption className={styles.lightbox__caption}>
            {current.caption && (
              <span className={styles["lightbox__caption-title"]}>{current.caption}</span>
            )}
            {current.subcaption && (
              <span className={styles["lightbox__caption-sub"]}>{current.subcaption}</span>
            )}
            {many && (
              <span className={styles.lightbox__counter}>
                {index + 1} / {images.length}
              </span>
            )}
          </figcaption>
        )}
      </figure>

      {many && (
        <button
          type="button"
          className={`${styles.lightbox__nav} ${styles["lightbox__nav--next"]}`}
          onClick={goNext}
          aria-label="Sonraki görsel"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
            <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </button>
      )}
    </div>,
    document.body
  );
}
